import './style.scss';
import { useAuth, useBasic } from 'contexts';
import { useCallback, useEffect, useState } from 'react';
import { getCoursesReports, getCompletedCourses, getTotalCourses, CourseData, TotalCourse, Course } from 'services';
import * as XLSX from 'xlsx';

interface Range {
  page: number;
  pages: number;
  count: number;
  request: string;
}

export default function CoursesReportsPage() {
  const { appearance } = useBasic();
  const { profileUser } = useAuth();

  const [courses, setCourses] = useState<Course[]>([]);
  const [data, setData] = useState<CourseData[]>([]);
  const [totalCourses, setTotalCourses] = useState<TotalCourse[]>([]);
  const [courseId, setCourseId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [search, setSearch] = useState('');
  const [sortType, setSortType] = useState('curso');
  const [sortReverse, setSortReverse] = useState(false);
  const [range, setRange] = useState<Range>({ page: 1, pages: 1, count: 0, request: 'items=0-100' });

  const isMenuActive = useCallback(
    (pathname: string) => {
      if (appearance?.menu && Object.keys(appearance?.menu as any).includes(pathname)) {
        return (appearance?.menu as any)[pathname];
      }
      return true;
    },
    [appearance],
  );

  useEffect(() => {
    if (profileUser?.roles?.includes('admin') === false) {
      window.location.href = '/';
    } else if (!isMenuActive('reports')) {
      window.location.href = '/';
    }
  }, [isMenuActive, profileUser]);

  const parseRange = useCallback(
    (contentRange: string) => {
      // Example: "items 0-99/1000"
      const match = contentRange.match(/items (\d+)-(\d+)\/(\d+)/);
      if (match) {
        const start = parseInt(match[1]);
        const end = parseInt(match[2]);
        const total = parseInt(match[3]);
        const page = Math.floor(start / 100) + 1;
        const pages = Math.ceil(total / 100);
        return { page, pages, count: total, request: range.request };
      }
      return range;
    },
    [range],
  );

  const listTotalCourses = useCallback(async () => {
    try {
      const data = await getTotalCourses({ courseId, dataInicio, dataFim });
      setTotalCourses(data);
    } catch (error) {
      console.log(error);
    }
  }, [courseId, dataInicio, dataFim]);

  const list = useCallback(async () => {
    try {
      const { data, contentRange } = await getCompletedCourses({ courseId, dataInicio, dataFim }, range.request);
      setData(data);
      if (contentRange) {
        setRange(parseRange(contentRange));
      }
      listTotalCourses();
    } catch (error) {
      console.log(error);
    }
  }, [courseId, dataInicio, dataFim, range.request, listTotalCourses, parseRange]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const data = await getCoursesReports();
        setCourses(data);
        list();
      } catch (error) {
        console.log(error);
      }
    };
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gotopage = (page: number) => {
    const newRequest = `items=${(page - 1) * 100}-${page * 100}`;
    setRange(prev => ({ ...prev, page, request: newRequest }));
    list();
  };

  const paginate = (direction: number) => {
    const newPage = range.page + direction;
    if (newPage > 0 && newPage <= range.pages) {
      gotopage(newPage);
    }
  };

  const handleExport = useCallback(async () => {
    try {
      const exportRange = `items=0-${range.count}`;
      const { data } = await getCompletedCourses({ courseId, dataInicio, dataFim }, exportRange);

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cursos Concluídos');
      XLSX.writeFile(workbook, 'cursos_concluidos.xlsx');
    } catch (error) {
      console.log(error);
    }
  }, [courseId, dataInicio, dataFim, range.count]);

  const handleSort = (type: string) => {
    if (sortType === type) {
      setSortReverse(!sortReverse);
    } else {
      setSortType(type);
      setSortReverse(false);
    }
  };

  const filteredData = data
    .filter(
      item =>
        item.curso.toLowerCase().includes(search.toLowerCase()) ||
        item.usuario.toLowerCase().includes(search.toLowerCase()) ||
        item.email.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      const aVal = (a as any)[sortType];
      const bVal = (b as any)[sortType];
      if (aVal < bVal) return sortReverse ? 1 : -1;
      if (aVal > bVal) return sortReverse ? -1 : 1;
      return 0;
    });

  return (
    <div className="p-4">
      <div className="mb-4">
        <h4 className="text-lg font-bold">Cursos Concluídos ({range.count})</h4>
        <p className="text-gray-600">Página de relatório cursos concluídos.</p>
      </div>

      <div className="bg-white p-4 rounded shadow mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Curso</label>
            <select value={courseId} onChange={e => setCourseId(e.target.value)} className="w-full p-2 border rounded">
              <option value="">Selecione</option>
              {courses.map(course => (
                <option key={course._id} value={course._id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Data início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={e => setDataInicio(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Data fim</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="w-full p-2 border rounded" />
          </div>
          <div className="flex items-end space-x-2">
            <button onClick={list} className="bg-blue-500 text-white px-4 py-2 rounded">
              Buscar
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Texto</label>
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow mb-4">
        <h4 className="text-lg font-bold mb-4">Total por curso:</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {totalCourses.map(obj => (
            <div key={obj._id} className="border p-4 rounded text-center">
              <p className="text-xl font-bold">{obj._id}</p>
              <span className="text-lg">Total: {obj.total}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow mb-4 max-h-[500px] overflow-auto">
        {filteredData.length > 0 && (
          <div className="text-right mb-5">
            <button onClick={handleExport} className="bg-green-500 text-white px-4 py-2 rounded">
              Exportar Excel
            </button>
          </div>
        )}
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 cursor-pointer" onClick={() => handleSort('curso')}>
                CURSO {sortType === 'curso' && (sortReverse ? '▲' : '▼')}
              </th>
              <th className="p-2 cursor-pointer" onClick={() => handleSort('usuario')}>
                USUÁRIO {sortType === 'usuario' && (sortReverse ? '▲' : '▼')}
              </th>
              <th className="p-2 cursor-pointer" onClick={() => handleSort('email')}>
                EMAIL {sortType === 'email' && (sortReverse ? '▲' : '▼')}
              </th>
              <th className="p-2 cursor-pointer" onClick={() => handleSort('data_completou')}>
                DATA COMPLETOU {sortType === 'data_completou' && (sortReverse ? '▲' : '▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((obj, index) => (
              <tr key={index} className="border-t">
                <td className="p-2 text-center">{obj.curso}</td>
                <td className="p-2 text-center">{obj.usuario}</td>
                <td className="p-2 text-center">{obj.email}</td>
                <td className="p-2 text-center">{obj.data_completou}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredData.length === 0 && <p className="text-center text-gray-500 m-4">Nenhum dado encontrado.</p>}
      </div>

      <div className="bg-white p-4 rounded shadow">
        <div className="flex items-center justify-center space-x-4">
          <span>Page</span>
          <input
            type="number"
            value={range.page}
            onChange={e => gotopage(Number(e.target.value))}
            className="w-16 p-1 border rounded text-center"
          />
          <span>of {range.pages}</span>
          <button onClick={() => paginate(-1)} className="px-2 py-1 border rounded" disabled={range.page <= 1}>
            ‹
          </button>
          <button onClick={() => paginate(1)} className="px-2 py-1 border rounded" disabled={range.page >= range.pages}>
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
